import { useCallback, useEffect, useRef, useState } from "react";
import { useRealtimeKitSelector } from "@cloudflare/realtimekit-react";
import type RTKClient from "@cloudflare/realtimekit";
import type { StageStatus } from "@cloudflare/realtimekit";

export function useLivestreamStageRequest({
  meeting,
}: {
  meeting: RTKClient | undefined;
}) {
  const stageStatus = useRealtimeKitSelector((m) => m.stage.status) as StageStatus;
  const prevStageStatusRef = useRef<StageStatus | undefined>(undefined);
  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!meeting) {
      prevStageStatusRef.current = stageStatus;
      return;
    }

    const prevStageStatus = prevStageStatusRef.current;

    if (
      stageStatus === "ACCEPTED_TO_JOIN_STAGE" &&
      prevStageStatus !== "ON_STAGE"
    ) {
      let cancelled = false;

      void (async () => {
        if (cancelled || meeting.stage.status !== "ACCEPTED_TO_JOIN_STAGE") {
          return;
        }

        try {
          await meeting.stage.join();
        } catch {
          return;
        }

        if (cancelled) return;
        if (meeting.self.permissions.canProduceVideo === "ALLOWED") {
          await meeting.self.enableAudio();
          await meeting.self.enableVideo();
        }
      })();

      prevStageStatusRef.current = stageStatus;
      return () => {
        cancelled = true;
      };
    }

    if (
      stageStatus === "OFF_STAGE" &&
      (prevStageStatus === "ON_STAGE" ||
        prevStageStatus === "ACCEPTED_TO_JOIN_STAGE")
    ) {
      void (async () => {
        if (meeting.self.audioEnabled) await meeting.self.disableAudio();
        if (meeting.self.videoEnabled) await meeting.self.disableVideo();
      })();
    }

    prevStageStatusRef.current = stageStatus;
  }, [meeting, stageStatus]);

  const requestJoin = useCallback(async () => {
    if (!meeting?.stage || requesting || stageStatus !== "OFF_STAGE") return;

    setRequesting(true);
    try {
      await meeting.stage.requestAccess();
    } finally {
      setRequesting(false);
    }
  }, [meeting, requesting, stageStatus]);

  const cancelRequestJoin = useCallback(async () => {
    if (!meeting?.stage || cancelling || stageStatus !== "REQUESTED_TO_JOIN_STAGE") {
      return;
    }

    setCancelling(true);
    try {
      await meeting.stage.cancelRequestAccess();
    } finally {
      setCancelling(false);
    }
  }, [cancelling, meeting, stageStatus]);

  const leaveStage = useCallback(async () => {
    if (!meeting?.stage || leaving || stageStatus !== "ON_STAGE") return;

    setLeaving(true);
    try {
      await meeting.stage.leave();
    } finally {
      setLeaving(false);
    }
  }, [meeting, leaving, stageStatus]);

  const canRequestJoin = stageStatus === "OFF_STAGE";
  const canCancelRequestJoin = stageStatus === "REQUESTED_TO_JOIN_STAGE";
  const canLeaveStage = stageStatus === "ON_STAGE";

  const requestJoinLabel = "Request Join";
  const cancelRequestJoinLabel = "Cancel join request";

  return {
    stageStatus,
    requesting,
    cancelling,
    leaving,
    requestJoin,
    cancelRequestJoin,
    leaveStage,
    canRequestJoin,
    canCancelRequestJoin,
    canLeaveStage,
    requestJoinLabel,
    cancelRequestJoinLabel,
  };
}
